import { useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import { Link, useParams } from "react-router-dom";
import { usePageTitle } from "@/hooks/use-page-title";
import { useTeams } from "@/hooks/use-teams";
import { useCanManageTeams } from "@/hooks/use-can-manage-teams";
import { CreateTeamDialog } from "@/components/teams/CreateTeamDialog";
import { RenameTeamDialog } from "@/components/teams/RenameTeamDialog";
import { DeleteTeamDialog } from "@/components/teams/DeleteTeamDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Loader2, MoreHorizontal, Users } from "lucide-react";

const PER_PAGE = 50;

export function TeamsPage() {
  usePageTitle("Teams");
  const { orgId, slug } = useParams<{ orgId: string; slug: string }>();
  const canManage = useCanManageTeams();

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounceValue(search, 300);
  const [page, setPage] = useState(1);
  const { teams, pagination, isPending } = useTeams({
    search: debouncedSearch,
    page,
    perPage: PER_PAGE,
  });

  const [renameTarget, setRenameTarget] = useState<{ id: string; name: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Teams</h1>
          <p className="text-muted-foreground">Group members for collaboration and assignments.</p>
        </div>
        {canManage && <CreateTeamDialog />}
      </div>

      <Input
        placeholder="Search teams…"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(1);
        }}
        className="max-w-sm"
      />

      {isPending ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : teams.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {search ? "No teams match your search." : "No teams yet."}
        </p>
      ) : (
        <div className="space-y-2">
          {teams.map((t) => (
            <div
              key={t.id}
              className="flex items-center justify-between rounded-lg border p-3"
            >
              <Link
                to={`/${orgId}/${slug}/settings/teams/${t.id}`}
                className="flex flex-1 items-center gap-3 hover:underline"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary">
                  <Users className="h-4 w-4 text-secondary-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium">{t.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {t.memberCount} {t.memberCount === 1 ? "member" : "members"}
                  </p>
                </div>
              </Link>
              {canManage && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" aria-label="Team actions">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setRenameTarget({ id: t.id, name: t.name })}>
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() => setDeleteTarget({ id: t.id, name: t.name })}
                    >
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ))}
        </div>
      )}

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Page {pagination.page} of {pagination.totalPages} · {pagination.total} total
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <RenameTeamDialog
        key={renameTarget?.id ?? "rename-closed"}
        team={renameTarget}
        open={!!renameTarget}
        onOpenChange={(o) => !o && setRenameTarget(null)}
      />
      <DeleteTeamDialog
        team={deleteTarget}
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      />
    </div>
  );
}
